import { ArrowCircleRight2 } from 'iconsax-react';
import { Box, Button, CardActionArea, Drawer, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MHidden } from 'components/@material-extend';
import Logo from 'components/Logo';
import NavSection from 'components/NavSection';
import Scrollbar from 'components/Scrollbar';
import useCollapseDrawer from 'hooks/useCollapseDrawer';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import sidebarConfig from './SidebarConfig';

const DRAWER_WIDTH = 240;
const COLLAPSE_WIDTH = 72;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex,
    }),
  },
}));

const LogoutButton = styled(Button)(({ theme }) => ({
  fontWeight: 'fontWeightMedium',
  fontSize: '14px',
  lineHeight: '24px',
  color: '#979CB7',
  justifyContent: 'unset',
  padding: 0,
  '&:hover': {
    color: 'white',
    bgcolor: 'transparent',
  },

  span: {
    marginLeft: '28px',
    marginRight: '12px',
  },
}));

IconCollapse.propTypes = {
  onToggleCollapse: PropTypes.func,
  collapseClick: PropTypes.bool,
};

function IconCollapse({ onToggleCollapse, collapseClick }) {
  return (
    <Tooltip title='Mini Menu'>
      <CardActionArea
        onClick={onToggleCollapse}
        sx={{
          width: 18,
          height: 18,
          display: 'flex',
          cursor: 'pointer',
          borderRadius: '50%',
          alignItems: 'center',
          color: 'text.primary',
          justifyContent: 'center',
          border: 'solid 1px currentColor',
          ...(collapseClick && {
            borderWidth: 2,
          }),
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: 'currentColor',
            transition: (theme) => theme.transitions.create('all'),
            ...(collapseClick && {
              width: 0,
              height: 0,
            }),
          }}
        />
      </CardActionArea>
    </Tooltip>
  );
}

DashboardSidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function DashboardSidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        paddingBottom: '16px',
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          px: 2.5,
          pt: 3,
          pb: 2,
          ...(isCollapse && {
            alignItems: 'center',
          }),
        }}
      >
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Box component={RouterLink} to='/' sx={{ display: 'inline-flex' }}>
            <Logo includeText={!isCollapse} includeVersion={false} />
          </Box>

          <MHidden width='lgDown'>
            {!isCollapse && <IconCollapse onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />}
          </MHidden>
        </Stack>
      </Stack>

      <NavSection
        navConfig={sidebarConfig.filter((item) => !item.bottom)}
        isShow={!isCollapse}
        sx={{
          marginTop: '58px',
          '& .MuiListItemButton-root': {
            fontWeight: 'fontWeightMedium',
            fontSize: '14px',
            lineHeight: '24px',
            color: '#979CB7',
            '&.active': { color: 'white', bgcolor: 'transparent' },
            '&:hover': {
              color: 'white',
              bgcolor: 'transparent',
            },
          },
        }}
      />
      <Box sx={{ flexGrow: 1 }} />
      <NavSection
        navConfig={sidebarConfig.filter((item) => item.bottom)}
        isShow={!isCollapse}
        sx={{
          marginTop: '58px',
          '& .MuiListItemButton-root': {
            fontWeight: 'fontWeightMedium',
            fontSize: '14px',
            lineHeight: '24px',
            color: '#979CB7',
            '&.active': { color: 'white', bgcolor: 'transparent' },
            '&:hover': {
              color: 'white',
              bgcolor: 'transparent',
            },
          },
        }}
      />
      <LogoutButton startIcon={<ArrowCircleRight2 size={24} />}>Logout</LogoutButton>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      <MHidden width='lgUp'>
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: { width: DRAWER_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width='lgDown'>
        <Drawer
          open
          variant='persistent'
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: '#161A2A',
              ...(isCollapse && {
                width: COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                borderRight: 0,
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
                boxShadow: (theme) => theme.customShadows.z20,
                bgcolor: '#161A2A',
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>
    </RootStyle>
  );
}
